import Image from "next/legacy/image"
import Link from "next/link"
import { useEffect, useState } from "react"
import { useCartContext } from "@/components/woocommerce/cartStore"
import { useMeta } from "@/lib/contexts/appContext"
import useLocalShop from "@/lib/hooks/useLocalShop"
import cx from "classnames"

// Components
import Button from "@/common/button/button"

// Lib
import { trackProductClick } from "@/lib/helpers/tagManager"
import { getProductResponse } from "@/lib/helpers/tagManager"

export default function Product({ product }) {
  const {
    id,
    type,
    name,
    images,
    short_description,
    price_html,
    permalink,
    is_purchasable,
    add_to_cart
  } = product

  const {
    woocommerce: {
      shop_enabled,
      i18n: { cards: { cta_text, read_more_text } = {} } = {}
    }
  } = useMeta()

  const [firstImage] = images
  const { nonce, isLoading, addCartItem, fetchCart } = useCartContext()
  const [cardLoading, setCardLoading] = useState(false)
  const [addProduct, setProduct] = useState(null)
  const { setCart } = useLocalShop()

  useEffect(() => {
    if (!nonce) {
      return
    }

    if (addProduct) {
      try {
        addCartItem(product)
      } finally {
        setProduct(null)
        setCardLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce])

  const handleOnAddToCart = async (product) => {
    setCardLoading(true)
    setCart()

    if (!nonce) {
      setProduct(product)
      await fetchCart()
      return
    }

    try {
      await addCartItem(product)
    } finally {
      setCardLoading(false)
    }
  }

  const handleProductClick = () => {
    trackProductClick(getProductResponse([product]))
  }

  if (!is_purchasable) {
    return null
  }

  return (
    <Card className={`post-${id} relative product bg-white w-full z-10`}>
      <Link
        href={permalink}
        prefetch={false}
        tabIndex="-1"
        aria-label={read_more_text}
        onClick={handleProductClick}>
        <CardImage
          src={firstImage ? firstImage.src : "/"}
          alt={name}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
      </Link>
      <div className="flex flex-col flex-grow p-5 lg:p-8">
        <Link href={permalink} prefetch={false} legacyBehavior>
          <a
            className="mb-4 text-black no-underline"
            onClick={handleProductClick}>
            <h4 className="font-sans">{name}</h4>
          </a>
        </Link>
        {short_description ? (
          <span
            dangerouslySetInnerHTML={{
              __html: short_description
            }}></span>
        ) : null}
        <Button
          tagName="a"
          href={permalink}
          ariaLabel={read_more_text}
          size="small"
          theme="text"
          onClick={handleProductClick}
          className="mt-4 mb-6">
          {read_more_text}
        </Button>
        {shop_enabled && (
          <footer className="mt-auto">
            <span
              className="flex mb-6 text-xl font-bold"
              dangerouslySetInnerHTML={{
                __html: price_html
              }}></span>
            {type === "variable" ? (
              <Button
                tagName="a"
                ariaLabel={add_to_cart.text}
                onClick={handleProductClick}
                disabled={isLoading}
                href={add_to_cart.url}
                className="w-full"
                theme="green">
                {add_to_cart.text}
              </Button>
            ) : (
              <Button
                className="w-full"
                ariaLabel={cta_text}
                loading={cardLoading}
                disabled={cardLoading || isLoading}
                onClick={() => handleOnAddToCart(product)}
                theme="green">
                {cta_text}
              </Button>
            )}
          </footer>
        )}
      </div>
    </Card>
  )
}

export function Card({ children, className }) {
  const classNames = cx("flex flex-col group", className)
  return <article className={classNames}>{children}</article>
}

export function CardImage({ src = null, alt = null, sizes = null }) {
  return (
    <figure>
      <Image
        src={src ? src : "placeholder"}
        sizes={sizes}
        alt={alt}
        layout="responsive"
        width="374"
        height="280"
        className="object-cover transition-transform transform group-hover:scale-105"
      />
    </figure>
  )
}
